import Swiper from "swiper";
import { A11y, Navigation, Keyboard } from "swiper/modules";
import { initializeOnApproach } from "../../../resources/js/_initialize-on-approach";

document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll(".tile-carousel").forEach((carousel) => {
    initializeOnApproach(carousel, () => initializeTileCarousel(carousel));
  });
});

document.addEventListener("blockEditorInit", function (event) {
  const block = event.detail.block;

  if (!block) {
    return;
  }

  if (block.matches && block.matches(".tile-carousel")) {
    initializeTileCarousel(block);
  }

  block.querySelectorAll(".tile-carousel").forEach((carousel) => {
    initializeTileCarousel(carousel);
  });
});

function initializeTileCarousel(carouselElement) {
  const swiperContainer = carouselElement.querySelector(
    ".tile-carousel__swiper",
  );
  const prevButton = carouselElement.querySelector(
    ".tile-carousel__nav-button--prev",
  );
  const nextButton = carouselElement.querySelector(
    ".tile-carousel__nav-button--next",
  );
  const swiperWrapper = swiperContainer?.querySelector(".swiper-wrapper");

  if (
    !swiperContainer ||
    swiperContainer.dataset.tileCarouselInitialized === "true"
  ) {
    return;
  }

  const slides = swiperContainer.querySelectorAll(".swiper-slide");

  if (slides.length < 2) {
    return;
  }

  swiperContainer.dataset.tileCarouselInitialized = "true";

  const swiperConfig = {
    modules: [A11y, Navigation, Keyboard],
    slidesPerView: 1.667,
    spaceBetween: 24,
    speed: 700,
    centeredSlides: true,
    loop: true,
    loopAdditionalSlides: 1,
    grabCursor: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    a11y: {
      enabled: true,
      prevSlideMessage: "Previous slide",
      nextSlideMessage: "Next slide",
    },
    breakpoints: {
      0: {
        slidesPerView: 1.2,
        spaceBetween: 16,
      },
      768: {
        slidesPerView: 1.4,
        spaceBetween: 20,
      },
      992: {
        slidesPerView: 1.667,
        spaceBetween: 24,
      },
    },
  };

  if (prevButton && nextButton) {
    swiperConfig.navigation = {
      prevEl: prevButton,
      nextEl: nextButton,
    };
  }

  let activeCardRevealTimeout = null;
  let activeCardTransitionEndHandler = null;

  const clearActiveCardRevealTimeout = () => {
    if (activeCardRevealTimeout) {
      window.clearTimeout(activeCardRevealTimeout);
      activeCardRevealTimeout = null;
    }
  };

  const clearActiveCardTransitionEndHandler = () => {
    if (!swiperWrapper || !activeCardTransitionEndHandler) {
      return;
    }

    swiperWrapper.removeEventListener(
      "transitionend",
      activeCardTransitionEndHandler,
    );
    activeCardTransitionEndHandler = null;
  };

  const clearPendingActiveCardReveal = () => {
    clearActiveCardRevealTimeout();
    clearActiveCardTransitionEndHandler();
  };

  const getAnimatedCards = () =>
    Array.from(
      carouselElement.querySelectorAll('.tile-carousel__card[data-aos="fade-up"]'),
    );

  const hideAllSlideCards = () => {
    getAnimatedCards().forEach((card) => {
      card.classList.remove("tile-carousel__card--visible");
      card.classList.remove("aos-animate");
    });
  };

  const getRevealDelay = (swiperInstance) => {
    const transitionSpeed = Number(swiperInstance?.params?.speed);

    if (!Number.isFinite(transitionSpeed) || transitionSpeed <= 0) {
      return 0;
    }

    return transitionSpeed;
  };

  const revealActiveSlideCard = () => {
    requestAnimationFrame(() => {
      const activeSlide = carouselElement.querySelector(".swiper-slide-active");

      if (!activeSlide) {
        return;
      }

      const card = activeSlide.querySelector(
        '.tile-carousel__card[data-aos="fade-up"]',
      );

      if (!card) {
        return;
      }

      // Commit the hidden state first so the reveal transition always replays.
      card.getBoundingClientRect();

      requestAnimationFrame(() => {
        card.classList.add("tile-carousel__card--visible");
        card.classList.add("aos-animate");
      });
    });
  };

  const queueActiveSlideCardReveal = (swiperInstance, waitForTransition) => {
    clearPendingActiveCardReveal();

    if (!waitForTransition || !swiperWrapper) {
      activeCardRevealTimeout = window.setTimeout(() => {
        activeCardRevealTimeout = null;
        revealActiveSlideCard();
      }, 0);

      return;
    }

    activeCardTransitionEndHandler = (event) => {
      if (event.target !== swiperWrapper || event.propertyName !== "transform") {
        return;
      }

      clearPendingActiveCardReveal();
      revealActiveSlideCard();
    };

    swiperWrapper.addEventListener("transitionend", activeCardTransitionEndHandler);

    activeCardRevealTimeout = window.setTimeout(() => {
      clearActiveCardTransitionEndHandler();
      activeCardRevealTimeout = null;
      revealActiveSlideCard();
    }, getRevealDelay(swiperInstance) + 80);
  };

  swiperConfig.on = {
    init: function () {
      hideAllSlideCards();
      queueActiveSlideCardReveal(this, false);
    },
    slideChangeTransitionStart: function () {
      clearPendingActiveCardReveal();
      hideAllSlideCards();
      queueActiveSlideCardReveal(this, true);
    },
  };

  const swiperInstance = new Swiper(swiperContainer, swiperConfig);
  const syncActiveSlideCardState = () => {
    hideAllSlideCards();
    queueActiveSlideCardReveal(swiperInstance, false);
  };

  if (document.readyState === "complete") {
    syncActiveSlideCardState();
  } else {
    window.addEventListener("load", syncActiveSlideCardState, { once: true });
  }
}
